// src/components/OAuthComplete.tsx
//
// Landing page genérica del single-callback pattern.
//
// El backend canónico (https://staging-backend.efficientai.es/api/v1/oauth/<provider>/callback)
// recibe el redirect de Meta/LinkedIn/Google después de que el user autorizó,
// hace exchange + persist, y hace 302 redirect aquí con:
//
//   /oauth-complete?provider=<x>&status=ok
//   /oauth-complete?provider=<x>&status=error&error=<code>&error_description=<text>
//
// Este componente NO hace exchange — los tokens YA están persistidos en
// el backend. Solo muestra UI de resultado y redirige al user al módulo
// correcto del dashboard.

import { useEffect, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

const PROVIDER_LABELS: Record<string, string> = {
  meta: 'Meta (Facebook + Instagram + WhatsApp Business)',
  linkedin: 'LinkedIn',
  'google-ads': 'Google Ads',
};

const PROVIDER_DESTINATIONS: Record<string, string> = {
  meta: '/m/mkt-meta/dashboard',
  linkedin: '/m/empresa/marketing-safety',
  'google-ads': '/m/google/ads',
};

export default function OAuthComplete() {
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const provider = params.get('provider') || 'unknown';
  const status = params.get('status') || 'unknown';
  const errorCode = params.get('error') || '';
  const errorDescription = params.get('error_description') || '';
  const pagesCount = params.get('pages_count') || '';
  const autoSelected = params.get('auto_selected') === '1';

  const providerLabel = PROVIDER_LABELS[provider] || provider;
  const destination = PROVIDER_DESTINATIONS[provider] || '/';

  // Auto-redirect to provider dashboard 3s after success
  const isSuccess = status === 'ok';
  useEffect(() => {
    if (!isSuccess) return;
    const t = setTimeout(() => navigate(destination), 3000);
    return () => clearTimeout(t);
  }, [isSuccess, destination, navigate]);

  const errorHumanLabel = useMemo(() => {
    const map: Record<string, string> = {
      state_expired: 'El proceso tardó demasiado (>10 min). Intenta conectar de nuevo.',
      state_invalid: 'La sesión OAuth se corrompió. Intenta desde el dashboard original.',
      missing_params: 'El proveedor no devolvió los parámetros esperados.',
      meta_api: 'El proveedor rechazó la autorización. Revisa los permisos otorgados.',
      meta_returned_error: errorDescription || 'El proveedor devolvió un error.',
    };
    return map[errorCode] || errorDescription || errorCode || 'Error desconocido.';
  }, [errorCode, errorDescription]);

  return (
    <div className="o-bg o-bg--light min-h-[100svh] flex items-center justify-center p-6">
      <div className="o-card max-w-lg w-full text-center space-y-4">
        {isSuccess ? (
          <>
            <div className="text-5xl">✅</div>
            <h1 className="text-2xl font-bold">Conexión exitosa</h1>
            <p className="text-sm opacity-80">
              {providerLabel} quedó conectado a tu cuenta.
              {pagesCount && (
                <>
                  {' '}Detectamos <strong>{pagesCount}</strong> página(s) autorizada(s)
                  {autoSelected && ', y se seleccionó la única disponible automáticamente'}
                  .
                </>
              )}
            </p>
            <p className="text-xs opacity-60">
              Redirigiendo al dashboard del módulo en 3 segundos…
            </p>
            <div>
              <button
                type="button"
                onClick={() => navigate(destination)}
                className="o-btn-ghost"
              >
                Ir ahora →
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="text-5xl">❌</div>
            <h1 className="text-2xl font-bold">No se pudo conectar</h1>
            <p className="text-sm opacity-80">
              Error conectando con {providerLabel}:
            </p>
            <div className="o-card text-left text-sm">
              <div className="font-mono text-xs opacity-70">{errorCode || 'unknown_error'}</div>
              <div className="mt-1">{errorHumanLabel}</div>
            </div>
            <div className="flex gap-2 justify-center">
              <Link to={destination} className="o-btn-ghost">
                Volver al dashboard
              </Link>
              <Link to="/" className="o-btn-ghost">
                Inicio
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
